import { useEffect, useState } from 'react';
import { ExpandMore } from '@material-ui/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import { TitleDivider } from '../../../components/titleDivider';
import {
  DetailsTable,
  DetailsTableField,
} from '../../../components/detailsTable';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { LEVERAGE_EXCEPTION_COLOR } from '../../../utils/constants';
import {
  isGroundUp,
  isStabilizedBridge,
  LoanSubType,
} from '@roc/feature-utils';
import { insertIf } from '@roc/feature-utils';
import { amortizationOptions } from '@roc/feature-loans';

interface LoanDetailsAccordionProps {
  store: QuoteTypeStore;
}

function getLoanDetail(store): DetailsTableField[] {
  const outputs = store.loanEconomicsStore.outputs;
  return [
    {
      label: isStabilizedBridge(store.loanSubtype)
        ? 'Loan Amount'
        : 'Initial Advance',
      value: (
        <Typography
          style={{
            color: store.loanEconomicsStore.enableManualQuote
              ? LEVERAGE_EXCEPTION_COLOR
              : undefined,
          }}
        >
          {formatCurrency(
            isStabilizedBridge(store.loanSubtype)
              ? outputs?.totalLoanAmount
              : outputs?.initialLoanAmount
          )}
        </Typography>
      ),
      colSpan: 1,
    },
    {
      label: 'Interest Payment Type',
      value: store.loanEconomicsStore.interestType,
      colSpan: 1,
    },
    ...insertIf(!isStabilizedBridge(store.loanSubtype), [
      {
        label: 'Construction Holdback',
        value: formatCurrency(
          outputs?.constructionHoldback - (outputs?.ltcCatchupAmount || 0)
        ),
        colSpan: 1,
      },
    ]),
    ...insertIf(isGroundUp(store.loanSubtype), [
      {
        label: 'LTC CatchUp Draw Amount',
        value: formatCurrency(outputs?.ltcCatchupAmount),
        colSpan: 1,
      },
    ]),
    {
      label: 'Interest Reserve',
      value: `${formatCurrency(outputs?.interestReserve)}`,
      colSpan:
        isStabilizedBridge(store.loanSubtype) || isGroundUp(store.loanSubtype)
          ? 2
          : 1,
    },
  ];
}

function getLoanDetailSingleProperty(store): DetailsTableField[] {
  const { pricerSummaryStore, dscrCalculatorStore } = store;
  const { submittedPricingOption } = pricerSummaryStore;
  const { formValues, pricerLoanTerms } = submittedPricingOption;
  const { amortization, prePaymentPenalty } = formValues;
  const {
    rateChargedToBorrower,
    monthlyPaymentWithoutExpenses,
    monthlyPayment,
  } = pricerLoanTerms;
  const { pitiaDscrInIOPeriod } = dscrCalculatorStore.calculatedValues;

  return [
    {
      label: 'Amortization Type',
      value: getAmortizationLabelFromValue(amortization),
      colSpan: 1,
    },
    {
      label: 'Pre-Payment Penalty',
      value: prePaymentPenalty,
      colSpan: 1,
    },
    {
      label: 'PITIA DSCR',
      value: pitiaDscrInIOPeriod,
      colSpan: 1,
    },
    {
      label: `Today's Rate`,
      value: `${formatPercentage(rateChargedToBorrower)}`,
      colSpan: 1,
    },
    {
      label: 'Monthly PI Payment',
      value: `${formatCurrency(monthlyPaymentWithoutExpenses)}`,
      colSpan: 1,
    },
    {
      label: 'Monthly PITIA Payment',
      value: `${formatCurrency(monthlyPayment)}`,
      colSpan: 1,
    },
  ];
}

function getLoanDetailRentalPortfolio(store): DetailsTableField[] {
  const { pricerSummaryStore, dscrCalculatorStore } = store;
  const { submittedPricingOption } = pricerSummaryStore;
  const { formValues, pricerLoanTerms } = submittedPricingOption;
  const { amortization, prePaymentPenalty } = formValues;
  const {
    rateChargedToBorrower,
    monthlyPaymentWithoutExpenses,
    monthlyPayment,
  } = pricerLoanTerms;
  const { pitiaDscrInIOPeriod } = dscrCalculatorStore.calculatedValues;
  return [
    {
      label: 'Amortization Type',
      value: getAmortizationLabelFromValue(amortization),
      colSpan: 1,
    },
    {
      label: 'Pre-Payment Penalty',
      value: prePaymentPenalty,
      colSpan: 1,
    },
    {
      label: 'PITIA DSCR',
      value: pitiaDscrInIOPeriod,
      colSpan: 1,
    },
    {
      label: `Today's Rate`,
      value: `${formatPercentage(rateChargedToBorrower)}`,
      colSpan: 1,
    },
    {
      label: 'Monthly PI Payment',
      value: `${formatCurrency(monthlyPaymentWithoutExpenses)}`,
      colSpan: 1,
    },
    {
      label: 'Monthly PITIA Payment',
      value: `${formatCurrency(monthlyPayment)}`,
      colSpan: 1,
    },
  ];
}

export const LoanDetailsAccordion = observer(
  ({ store }: LoanDetailsAccordionProps) => {
    const [expanded, setExpanded] = useState(true);

    function loanDetails(): DetailsTableField[] {
      switch (store.loanSubtype) {
        case LoanSubType.SINGLE_PROPERTY:
          return getLoanDetailSingleProperty(store);
        case LoanSubType.RENTAL_PORTFOLIO:
          return getLoanDetailRentalPortfolio(store);
        default:
          return getLoanDetail(store);
      }
    }

    useEffect(() => {
      store.loanEconomicsStore.setInterestType();
    }, [store.loanEconomicsStore.totalLoanAmount]);
    return (
      <Accordion
        expanded={expanded}
        onChange={(e, expanded) => setExpanded(expanded)}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="large" />}
          aria-controls="loan-details-accordion"
        >
          <TitleDivider title="Loan Details" />
        </AccordionSummary>
        <AccordionDetails>
          <DetailsTable fields={loanDetails()} />
        </AccordionDetails>
      </Accordion>
    );
  }
);

const getAmortizationLabelFromValue = value => {
  const option = amortizationOptions.find(opt => opt.value === value);
  return option ? option.label : value;
};
